import axios from "axios";
import { memo, useEffect, useState } from "react";
import { GiCheckMark } from "react-icons/gi";
import AddIcon from "../../../../Assets/ReactIcons/AddIcon";
import ViewIcon from "../../../../Assets/ReactIcons/ViewIcon";
import excel_icon from "../../../../Assets/icons/Excel.png";
import uploadFile from "../../../../Assets/icons/fileupload.png";
import { replaceUnderscore } from "../../../../CommonFunction/common";
import PortalFooter from "../../../Components/Reactportal/PortalFooter";
import Reactportal from "../../../Components/Reactportal/Reactportal";
import "./FileUpload.scss";
import { useModalContext } from "../../../Components/Reactportal/ModalContext";

// ← We still use react-drag-drop-files for the UI, but we’ll defer uploading until “Upload” is clicked.
import { FileUploader } from "react-drag-drop-files";
import { FaDownload, FaTrash } from "react-icons/fa";

function FileUpload({
  LoadForm,
  e,
  sendFile, // callback to parent for final uploaded URL
  allFieldsData,
  title,
  TypeOfPortal,
  displayTable,
}) {
  const [toggle, settoggle] = useState(false);
  // `img` holds the **already‐uploaded** URL (if any) from props or after successful upload.
  const [img, setImg] = useState(allFieldsData ? allFieldsData[e.name] : "");

  // Keep track of whether the upload request is in flight
  const [loadingState, setLoadingState] = useState(false);

  useEffect(() => {
    // If parent’s allFieldsData changes, reset `img` accordingly
    setImg(allFieldsData ? allFieldsData[e.name] : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allFieldsData]);

  // NOTE: The actual upload‐to‐backend is now done in FileUploadContainer
  // so we no longer call axios.post() from here.

  return (
    <div className="file-upload-container">
      {title !== "Add" ? (
        <div key={e.id} className={img ? "sectionview" : "sectionadd"}>
          <p className="filename">
            {e.label ? replaceUnderscore(e.label) : !displayTable ? replaceUnderscore(e.name) : ""}
            {img ? (
              <div onClick={() => settoggle((prev) => !prev)}>
                <GiCheckMark className={"fileTick"} />
              </div>
            ) : null}
          </p>
          <p className="status">
            {img ? (
              <div onClick={() => settoggle(true)}>
                <ViewIcon className={"Viewicon"} />
              </div>
            ) : (
              <div onClick={() => settoggle(true)}>
                <AddIcon className={"Addicon"} />
              </div>
            )}
          </p>
        </div>
      ) : (
        <>
          <img
            onClick={() => settoggle((prev) => !prev)}
            style={{ height: "80px" }}
            src={img ? img : uploadFile}
            alt="fileupload"
          />
          <p>{e.label}</p>
        </>
      )}

      {toggle && (
        <FileUploadContainer
          toggle={toggle}
          settoggle={settoggle}
          img={img}
          setImg={setImg}
          e={e}
          sendFile={sendFile}
          loadingState={loadingState}
          setLoadingState={setLoadingState}
        />
      )}
    </div>
  );
}

export default memo(FileUpload);

// ─────────────────────────────────────────────────────────
// FileUploadContainer is responsible for:
//  1) letting the user pick/drag a file → show local preview (no upload yet)
//  2) when “Upload” button is clicked, actually POST to backend
//  3) after success, call sendFile(...) and update `img`
//  4) let the user delete an already‐uploaded file
// ─────────────────────────────────────────────────────────
const FileUploadContainer = ({
  toggle,
  settoggle,
  img, // the already‐uploaded URL (if any)
  setImg, // setter to update img when upload succeeds or delete
  e,
  sendFile, // callback to parent
  loadingState,
  setLoadingState,
}) => {
  const Form_Key = "Upload_Files";
  const { pushModal, popModal } = useModalContext();

  // ─── Modal life cycle ─────────────────────────────────
  useEffect(() => {
    pushModal(Form_Key);
    return () => {
      popModal(Form_Key);
    };
  }, [pushModal, popModal]);

  const closeModal = () => {
    settoggle(false);
  };

  // ─── Local preview + file tracking ────────────────────
  // `localFile` = the File object selected/dragged by the user
  // `previewURL` = a temporary blob URL via URL.createObjectURL(localFile)
  const [localFile, setLocalFile] = useState(null);
  const [previewURL, setPreviewURL] = useState("");

  const onFileSelect = (file) => {
    // file is a File object from the FileUploader
    if (!file) {
      return;
    }

    // Revoke any old preview URL
    if (previewURL) {
      URL.revokeObjectURL(previewURL);
    }

    // Create new preview URL
    const blobURL = URL.createObjectURL(file);
    setPreviewURL(blobURL);
    setLocalFile(file);
  };

  // ─── Upload to backend when “Upload” (PortalFooter) is clicked ───
  const handleSubmitUpload = async () => {
    if (!localFile) {
      alert("Please select a file first.");
      return;
    }

    try {
      setLoadingState(true);
      const tempPath = window.location.pathname.split("/")[1] || "";
      const formData = new FormData();
      formData.append("file", localFile);

      const response = await axios.post(`${process.env.REACT_APP_API_URL}/upload/single`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          path: tempPath,
        },
      });

      const backendURL = response.data.fileURL;
      // 1) Inform parent via sendFile(...)
      sendFile(backendURL);
      // 2) Update our `img` so future opens show the uploaded file
      setImg(backendURL);

      // 3) Clear localFile + previewURL so we don’t keep showing the temporary blob
      setLocalFile(null);
      URL.revokeObjectURL(previewURL);
      setPreviewURL("");

      setLoadingState(false);
      // Optionally close modal after successful upload:
      // settoggle(false);
    } catch (error) {
      console.error("Error uploading file:", error);
      setLoadingState(false);
    } finally {
      closeModal();
    }
  };

  // ─── Delete from backend (existing URL) ─────────────────────
  const handleFileDelete = async () => {
    if (!img) {
      return;
    }
    try {
      setLoadingState(true);
      await axios.delete(`${process.env.REACT_APP_API_URL}/upload/file/delete`, {
        data: { url: img },
      });
      // 1) Tell parent it’s gone
      sendFile("");
      // 2) Clear our stored img
      setImg("");
      setLoadingState(false);
    } catch (err) {
      console.error("Error deleting file:", err);
      setLoadingState(false);
    }
  };

  // ─── Decide which “preview” to render: localFile vs. already‐uploaded img vs. default icon ──
  const renderPreview = () => {
    if (previewURL) {
      // Show the local, not‐yet‐uploaded file
      return <img src={previewURL} alt="Local preview" />;
    } else if (img) {
      // Show the previously‐uploaded file (or its Excel icon)
      const srcToShow = img.includes(".xls") || img.includes(".xlsx") ? excel_icon : img;
      return <img src={srcToShow} alt="Uploaded preview" />;
    } else {
      // No file chosen or uploaded yet: show the generic uploadFile icon + text
      return (
        <>
          <img src={uploadFile} alt="Upload Icon" style={{ width: 80, height: 80 }} />
          <p className="drag-drop-text">Drag &amp; Drop or Click to Upload</p>
        </>
      );
    }
  };


  // ─── “Trash” click logic ────────────────────────────
  const onTrashClick = async () => {
    if (previewURL) {
      // If there's a local preview (not yet uploaded), just clear it:
      URL.revokeObjectURL(previewURL);
      setPreviewURL("");
      setLocalFile(null);
    } else if (img) {
      // If there’s an already‐uploaded img URL, delete from backend
      try {
        setLoadingState(true);
        await axios.delete(`${process.env.REACT_APP_API_URL}/upload/file/delete`, {
          data: { url: img },
        });
        // Tell parent it’s gone, and clear our `img`
        sendFile("");
        setImg("");
        setLoadingState(false);
      } catch (err) {
        console.error("Error deleting file:", err);
        setLoadingState(false);
      } finally {
        closeModal();
      }
    }
  };
  return (
    <Reactportal open={toggle} onClose={closeModal} TypeOfPortal={"File Upload"}>
      <div className="filterUploadBoxContent">
        {(previewURL || img) && (
          <div className="preview-actions">
            {/* Show Download only if there’s an already‐uploaded img */}
            {img && (
              <a href={img} target="_blank" rel="noopener noreferrer">
                <FaDownload />
              </a>
            )}
            {/* Trash: clears local preview if present, otherwise deletes from backend */}
            <FaTrash onClick={onTrashClick} />
          </div>
        )}
        <div className="iconDiv boxcenter">
          <FileUploader
            handleChange={onFileSelect}
            name="file"
            types={[
              // Original formats
              "JPG",
              "PNG",
              "GIF",
              "XLS",
              "XLSX",
              "PDF",
              "DOC",
              "DOCX",
              "JPEG",

              // Image formats
              "BMP",
              "TIFF",
              "TIF",
              "SVG",
              "WEBP",
              "HEIC",
              "HEIF",
              "PSD",
              "EPS",
              "AI",

              // Spreadsheet / Data files
              "CSV",
              "ODS",
              "JSON",
              "XML",

              // Presentation formats
              "PPT",
              "PPTX",
              "ODP",



              // Archive / Compressed
              "ZIP",
              "RAR",
              "7Z",
              "TAR",
              "TAR.GZ",
              "TGZ",
              "GZ",
              "BZ2",



              // Other document types
              "ODT",
              "EPUB",
              "MOBI"
            ]}
            multiple={false}
          >
            <div className="drag-drop-area">{renderPreview()}</div>
          </FileUploader>

          <br />
          {/* Show the label as a link if there’s a previously‐uploaded file (img) */}
          <a href={img || "#"} target="_blank" rel="noreferrer noopener">
            {img ? e.label : null}
          </a>
        </div>
      </div>

      <PortalFooter
        renderContent={() => <></>}
        // disable the “Upload” button if no localFile selected or if an upload/delete is in progress
        disabled={loadingState || !localFile}
        // when “Upload” is clicked, call handleSubmitUpload
        submitform={handleSubmitUpload}
        submitType={"Save"}
        Cancletype="Cancel"
        Form_key={Form_Key}
        handleformclose={closeModal}
        // We no longer need the special POD-based disableupload logic—PortalFooter’s `disabled` covers it.
        loadingState={loadingState}
      />
    </Reactportal>
  );
};
